import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '../../components/ui/Button'
import { Card, CardHeader, CardBody } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { Modal } from '../../components/ui/Modal'
import { formatMoney, formatDate } from '../../lib/utils'
import { ComoCalculamosModal } from './ComoCalculamos'
import type { FaturaDoMes, Planejamento, PlanejamentoCompromisso, PlanejamentoTransferencia } from '../../types'

/** Ícone "?" com explicação curta ao passar o mouse ou tocar (title). */
export function Dica({ texto }: { texto: string }) {
  return (
    <span title={texto} tabIndex={0} aria-label={texto} className="ml-1 inline-flex cursor-help align-middle text-muted">
      <i className="ri-question-line text-sm" aria-hidden />
    </span>
  )
}

const STATUS: Record<PlanejamentoCompromisso['status'], { label: string; variant: 'income' | 'warning' | 'expense' }> = {
  paga: { label: 'Paga', variant: 'income' },
  a_pagar: { label: 'A pagar', variant: 'warning' },
  atrasada: { label: 'Atrasada', variant: 'expense' },
}

const TIPO_COMPROMISSO: Record<string, string> = {
  fatura: 'Fatura do cartão', financiamento: 'Parcela de financiamento', atrasada: 'Conta atrasada', fixa: 'Conta fixa', conta: 'Conta do mês',
}

function ItemConta({ descricao, detalhe, valor, status }: { descricao: string; detalhe: string; valor: number; status: PlanejamentoCompromisso['status'] }) {
  const st = STATUS[status]
  return (
    <li className="flex items-start justify-between gap-3 py-2">
      <div className="min-w-0">
        <p className="truncate text-13 font-medium text-white">{descricao}</p>
        <p className="text-2xs text-muted">{detalhe}</p>
      </div>
      <div className="flex flex-shrink-0 flex-col items-end gap-1">
        <span className="text-13 font-semibold text-white">{formatMoney(valor)}</span>
        <Badge variant={st.variant}>{st.label}</Badge>
      </div>
    </li>
  )
}

const itemCompromisso = (c: PlanejamentoCompromisso) => (
  <ItemConta key={`${c.origem}-${c.id}-${c.vencimento}`} descricao={c.descricao} detalhe={`${TIPO_COMPROMISSO[c.tipo] ?? c.tipo} · vence ${formatDate(c.vencimento)}`} valor={c.valor} status={c.status} />
)
const itemFatura = (f: FaturaDoMes) => (
  <ItemConta key={f.id} descricao={f.descricao} detalhe={`vence ${formatDate(f.vencimento)}`} valor={f.valor} status={f.status} />
)

/** Linha que abre e fecha o detalhe. */
function Expansor({ titulo, valor, dica, children, nivel = 0 }: { titulo: string; valor: number; dica?: string; children: ReactNode; nivel?: number }) {
  const [aberto, setAberto] = useState(false)
  return (
    <div className={nivel ? 'rounded bg-surface2 px-3' : ''}>
      <button type="button" onClick={() => setAberto((v) => !v)} aria-expanded={aberto}
        className="flex w-full items-center justify-between gap-3 py-2.5 text-left text-13 hover:text-white">
        <span className="flex items-center gap-1 font-medium text-white">
          {titulo}{dica && <Dica texto={dica} />}
        </span>
        <span className="flex items-center gap-2 whitespace-nowrap font-semibold text-white">
          {formatMoney(valor)}<i className={`${aberto ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-muted`} aria-hidden />
        </span>
      </button>
      {aberto && <div className="pb-3">{children}</div>}
    </div>
  )
}

function CartaoTransferencia({ t, desfeita, ocupado, onDesfazer, onRefazer }: {
  t: PlanejamentoTransferencia; desfeita: boolean; ocupado: boolean; onDesfazer: () => void; onRefazer: () => void
}) {
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 py-2">
      <div className="min-w-0">
        <p className="text-13 text-white">
          <strong>{formatMoney(t.valor)}</strong>: {t.saida.pessoa} → {t.entrada.pessoa}
        </p>
        <p className="text-2xs text-muted">
          {t.saida.descricao} ({formatDate(t.saida.data)}) e {t.entrada.descricao} ({formatDate(t.entrada.data)})
        </p>
        {desfeita && <p className="text-2xs text-warning">Desfeito: agora conta como renda e gasto.</p>}
      </div>
      {desfeita
        ? <Button size="sm" variant="light" icon="ri-link" loading={ocupado} onClick={onRefazer}>Refazer</Button>
        : <Button size="sm" variant="light" icon="ri-link-unlink" loading={ocupado} onClick={onDesfazer}>Desfazer</Button>}
    </li>
  )
}

/** Ícone ⓘ que abre o passo a passo do cálculo. */
function BotaoComo({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} aria-label="Como esse número é calculado" title="Como esse número é calculado"
      className="ml-1.5 inline-flex align-middle text-primary hover:opacity-80">
      <i className="ri-information-line text-base" aria-hidden />
    </button>
  )
}

const NOME_MES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro']

export interface PlanejamentoPanelProps {
  p: Planejamento
  mes: number
  ano: number
  onCriarOrcamento: (categoriaId: string) => void
  onDesfazerPar: (id: string) => void
  onRefazerPar: (id: string) => void
  parOcupado: string | null
  desfeitos: PlanejamentoTransferencia[]
}

/** Topo do Orçamento: Plano do mês (pela data da compra) e Caixa (quando o dinheiro sai). docs/specs/orcamento-plano-caixa-projecao.md */
export function PlanejamentoPanel({ p, mes, ano, onCriarOrcamento, onDesfazerPar, onRefazerPar, parOcupado, desfeitos }: PlanejamentoPanelProps) {
  const [rendaAberta, setRendaAberta] = useState(false)
  const [explicar, setExplicar] = useState<'plano' | 'caixa' | null>(null)
  const plano = p.planoDoMes
  const caixa = p.caixaDoMes
  if (!plano || !caixa) return null

  const periodo = `mes=${mes}&ano=${ano}`
  const fora = plano.gastoEPrevisto.foraDosOrcamentos
  const semCat = fora.detalhe.semCategoria
  const catsFora = fora.detalhe.categoriasSemOrcamento
  const veio = plano.veioDeMesesAnteriores
  const idsDesfeitos = new Set(desfeitos.map((d) => d.id))
  const parLista = [...p.transferenciasEntreVoces.filter((t) => !idsDesfeitos.has(t.id)), ...desfeitos]
  const mesSeguinte = NOME_MES[mes % 12]
  const gastoEPrevisto = plano.gastoDoMes + plano.aindaAGastarNosOrcamentos + plano.previstoForaDosOrcamentos

  return (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardBody className="space-y-3">
            <div>
              <p className="flex items-center text-base font-semibold text-white">
                Plano do mês<BotaoComo onClick={() => setExplicar('plano')} />
              </p>
              <p className="text-2xs text-muted">Renda {formatMoney(plano.rendaDoMes)} − gasto e previsto {formatMoney(gastoEPrevisto)}</p>
            </div>
            <div>
              <p className={`text-3xl font-bold ${plano.sobraPrevista >= 0 ? 'text-income' : 'text-expense'}`}>{formatMoney(plano.sobraPrevista)}</p>
              <p className="text-13 text-muted">
                {plano.sobraPrevista >= 0 ? 'sobra prevista' : 'falta prevista'}
                {plano.aindaAGastarNosOrcamentos > 0 && ` · faltam ${formatMoney(plano.aindaAGastarNosOrcamentos)} nos orçamentos`}
              </p>
            </div>
            {plano.noCartao.valor > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded bg-surface2 px-3 py-2 text-13">
                <span className="rounded bg-info/15 px-2 py-0.5 font-semibold text-info">No cartão {plano.noCartao.percentual.toFixed(0)}%</span>
                <span className="text-white">
                  {formatMoney(plano.noCartao.valor)} · faturas de {mesSeguinte}
                  {plano.noCartao.jaEmFaturaQueVenceNoMes > 0 && <span className="text-muted"> ({formatMoney(plano.noCartao.jaEmFaturaQueVenceNoMes)} já vence neste mês)</span>}
                </span>
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardBody className="space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="flex items-center text-base font-semibold text-white">
                  Caixa do mês<BotaoComo onClick={() => setExplicar('caixa')} />
                </p>
                <p className="text-2xs text-muted">Quando o dinheiro sai da conta</p>
              </div>
              <p className={`whitespace-nowrap text-2xl font-bold ${caixa.caixaPrevisto >= 0 ? 'text-income' : 'text-expense'}`}>{formatMoney(caixa.caixaPrevisto)}</p>
            </div>
            <ul className="space-y-1 text-13">
              {([
                ['Renda nas contas', caixa.rendaDoMes, '+'],
                ['Faturas que vencem', caixa.faturasQueVencem, '−'],
                ['À vista no mês', caixa.aVistaDoMes, '−'],
                ['Previsto à vista nos orçamentos', caixa.previstoAVistaNosOrcamentos, '−'],
                ['Contas sem orçamento a pagar', caixa.previstoForaDosOrcamentos, '−'],
                ['Em aberto de meses anteriores', caixa.emAberto, '−'],
              ] as const).filter(([, v, s]) => s === '+' || v > 0).map(([l, v, s]) => (
                <li key={l} className="flex justify-between gap-3"><span className="text-muted">{s} {l}</span><span className="text-white">{formatMoney(v)}</span></li>
              ))}
            </ul>
            {caixa.divergeDoPlano && (
              <div className="rounded border border-warning/60 p-2.5 text-13">
                <p className="font-medium text-warning">
                  {plano.sobraPrevista >= 0 ? 'Plano positivo, caixa negativo' : 'Plano negativo, caixa positivo'}
                </p>
                <p className="text-muted">
                  {plano.sobraPrevista >= 0
                    ? 'As faturas que vencem agora são de compras de meses anteriores.'
                    : 'Parte do que você gastou neste mês só sai do caixa nas próximas faturas.'}
                </p>
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardBody className="divide-y divide-border/40 py-1">
          <Expansor titulo="Gasto e previsto no mês" valor={gastoEPrevisto} dica="O que já foi gasto mais o que ainda falta nos orçamentos e nas contas sem orçamento.">
            <ul className="divide-y divide-border/40">
              {plano.gastoEPrevisto.nosOrcamentos.map((o) => (
                <li key={o.orcamentoId} className="flex items-center justify-between gap-3 py-2 text-13">
                  <span className="text-white">{o.categoria}{o.percentualNoCartao > 0 && <span className="ml-2 text-2xs text-info">{o.percentualNoCartao.toFixed(0)}% no cartão</span>}</span>
                  <span className="text-muted">gasto <span className="text-white">{formatMoney(o.gasto)}</span>{o.aindaAGastar > 0 && <> · falta <span className="text-white">{formatMoney(o.aindaAGastar)}</span></>}</span>
                </li>
              ))}
              <li className="flex items-center justify-between gap-3 py-2 text-13">
                <span className="text-white">Fora dos orçamentos</span>
                <span className="text-muted">gasto <span className="text-white">{formatMoney(fora.gasto)}</span>{fora.previsto > 0 && <> · a pagar <span className="text-white">{formatMoney(fora.previsto)}</span></>}</span>
              </li>
            </ul>
            {fora.itensPrevistos.length > 0 && <ul className="mt-1 divide-y divide-border/40 pl-3">{fora.itensPrevistos.map(itemCompromisso)}</ul>}
          </Expansor>
          <Expansor titulo="Veio de meses anteriores" valor={veio.faturasQueVencem} dica="Faturas que vencem neste mês, com as parcelas de compras antigas. Elas contaram no mês da compra, por isso não entram no Plano.">
            {veio.faturas.length === 0 ? <p className="py-1 text-13 text-muted">Nenhuma fatura vence neste mês.</p> : <ul className="divide-y divide-border/40">{veio.faturas.map(itemFatura)}</ul>}
            <div className="mt-2">
              <Expansor titulo="Em aberto de meses anteriores" valor={veio.emAberto.valor} nivel={1} dica="Contas atrasadas e faturas vencidas que ainda não foram pagas. Saem do caixa deste mês.">
                {veio.emAberto.valor === 0 ? <p className="text-13 text-muted">Nada em aberto.</p> : (
                  <ul className="divide-y divide-border/40">{veio.emAberto.atrasadas.map(itemCompromisso)}{veio.emAberto.faturasNaoPagas.map(itemFatura)}</ul>
                )}
              </Expansor>
            </div>
          </Expansor>
          <div className="py-2 text-13">
            <button type="button" onClick={() => setRendaAberta(true)} className="text-primary hover:underline">De onde vem a renda do mês</button>
          </div>
        </CardBody>
      </Card>

      <Link to="/orcamento/proximos-meses" className="block">
        <Button className="w-full" icon="ri-line-chart-line">Ver o caixa e os próximos meses</Button>
      </Link>

      {(semCat.quantidade > 0 || catsFora.length > 0) && (
        <Card className="border border-warning/30">
          <CardHeader title={<>Fora do orçamento<Dica texto="Gastos do mês que não estão dentro de nenhum orçamento. Eles pesam no seu mês do mesmo jeito." /></>} icon="ri-focus-3-line" actions={<span className="text-13 font-medium text-warning">{formatMoney(fora.detalhe.total)}</span>} />
          <CardBody className="divide-y divide-border/40 py-2">
            {semCat.quantidade > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-3 py-2.5">
                <p className="text-13 text-white">
                  Sem categoria <span className="text-muted">({semCat.quantidade} {semCat.quantidade === 1 ? 'lançamento' : 'lançamentos'})</span>
                  <span className="ml-2 font-semibold">{formatMoney(semCat.valor)}</span>
                </p>
                <Link to={`/transacoes?semCategoria=1&${periodo}`}><Button size="sm" variant="soft-primary" icon="ri-price-tag-3-line">Categorizar</Button></Link>
              </div>
            )}
            {catsFora.map((c) => (
              <div key={c.categoriaId} className="flex flex-wrap items-center justify-between gap-3 py-2.5">
                <p className="text-13 text-white">
                  {c.nome} <span className="text-muted">({c.quantidade} {c.quantidade === 1 ? 'lançamento' : 'lançamentos'})</span>
                  <span className="ml-2 font-semibold">{formatMoney(c.valor)}</span>
                </p>
                <div className="flex gap-2">
                  <Link to={`/transacoes?categoriaId=${c.categoriaId}&${periodo}`}><Button size="sm" variant="light" icon="ri-list-check-2">Ver lançamentos</Button></Link>
                  <Button size="sm" variant="soft-primary" icon="ri-add-line" onClick={() => onCriarOrcamento(c.categoriaId)}>Criar orçamento</Button>
                </div>
              </div>
            ))}
          </CardBody>
        </Card>
      )}

      {p.casalAtivo && (
        <Card>
          <CardHeader title={<>Entre vocês<Dica texto="Dinheiro que uma pessoa mandou e a outra recebeu. Não é renda nem gasto do casal, por isso fica fora da conta." /></>} icon="ri-arrow-left-right-line" />
          <CardBody>
            <p className="mb-2 text-13 text-muted">Somando: {p.pessoas.map((x) => x.nome).join(' e ')}.</p>
            {parLista.length === 0 ? <p className="text-13 text-muted">Nenhuma transferência entre vocês neste mês.</p> : (
              <ul className="divide-y divide-border/40">
                {parLista.map((t) => (
                  <CartaoTransferencia
                    key={t.id} t={t} desfeita={idsDesfeitos.has(t.id)} ocupado={parOcupado === t.id}
                    onDesfazer={() => onDesfazerPar(t.id)} onRefazer={() => onRefazerPar(t.id)}
                  />
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      )}

      {explicar && <ComoCalculamosModal plano={plano} caixa={caixa} primeiro={explicar} onClose={() => setExplicar(null)} />}

      <Modal open={rendaAberta} onClose={() => setRendaAberta(false)} title="De onde vem a renda do mês" size="md" footer={<Button variant="light" onClick={() => setRendaAberta(false)}>Fechar</Button>}>
        <div className="space-y-3">
          <p className="text-13 text-muted">
            Já lançado: <strong className="text-white">{formatMoney(p.rendaLancada)}</strong> · Ainda previsto (receitas fixas): <strong className="text-white">{formatMoney(p.rendaRecorrenteAPrever)}</strong>
          </p>
          {p.rendaItens.length === 0 ? <p className="text-13 text-muted">Nenhuma renda neste mês.</p> : (
            <ul className="divide-y divide-border/60">
              {p.rendaItens.map((r, i) => (
                <li key={`${r.descricao}-${r.data}-${i}`} className="flex items-center justify-between gap-3 py-2">
                  <div className="min-w-0">
                    <p className="truncate text-13 font-medium text-white">{r.descricao}</p>
                    <p className="text-2xs text-muted">{formatDate(r.data)} · {r.origem === 'recorrente' ? 'receita fixa' : 'lançada'}</p>
                  </div>
                  <div className="flex flex-shrink-0 flex-col items-end gap-1">
                    <span className="text-13 font-semibold text-income">{formatMoney(r.valor)}</span>
                    <Badge variant={r.status === 'confirmada' ? 'income' : r.status === 'pendente' ? 'warning' : 'muted'}>
                      {r.status === 'confirmada' ? 'Recebida' : r.status === 'pendente' ? 'Pendente' : 'Prevista'}
                    </Badge>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Modal>
    </div>
  )
}
